// Fotos que vão para o Google (Gabarito, Postagens, Produtos) só são aceitas em
// JPG/PNG, com pelo menos 250 px no lado curto, entre 10 KB e 5 MB. Fora disso o
// Google responde "500 Internal error" e o post falha (HIDROLANDIA AUTO PEÇAS
// ficou de 02/09 a 07/10/2026 sem post por causa de uma foto WebP).
// Por isso toda foto já sai do navegador no formato certo: o que não serve como
// está vira JPG; foto pequena é ampliada em vez de recusada.

export const MIN_LADO_CURTO = 250;
const MIN_BYTES = 10 * 1024;
const MAX_BYTES = 5 * 1024 * 1024;
const MAX_LADO = 2048;
// lado curto usado quando o JPG sai abaixo de 10 KB (foto lisa): ampliar dá bytes
const LADOS_PARA_PASSAR_DE_10KB = [720, 1080];

export type PlanoDaFoto = {
  /** JPG/PNG que já serve: vai como está, sem recomprimir. */
  manterOriginal: boolean;
  largura: number;
  altura: number;
  ampliada: boolean;
};

const ehFormatoAceito = (tipo: string) => tipo === "image/jpeg" || tipo === "image/png";

/** Decide o que fazer com a foto (puro, testável). */
export const planejarFotoParaGoogle = (foto: {
  tipo: string;
  bytes: number;
  largura: number;
  altura: number;
}): PlanoDaFoto => {
  const curto = Math.min(foto.largura, foto.altura);
  const longo = Math.max(foto.largura, foto.altura);
  const manterOriginal =
    ehFormatoAceito(foto.tipo) &&
    curto >= MIN_LADO_CURTO &&
    foto.bytes >= MIN_BYTES &&
    foto.bytes <= MAX_BYTES;
  if (manterOriginal) {
    return { manterOriginal, largura: foto.largura, altura: foto.altura, ampliada: false };
  }
  let escala = 1;
  if (curto < MIN_LADO_CURTO) escala = MIN_LADO_CURTO / curto;
  else if (longo > MAX_LADO) escala = MAX_LADO / longo;
  return {
    manterOriginal: false,
    largura: Math.round(foto.largura * escala),
    altura: Math.round(foto.altura * escala),
    ampliada: escala > 1,
  };
};

export const nomeComExtensaoJpg = (nome: string) => nome.replace(/\.[^.]+$/, "") + ".jpg";

const ehHeic = (file: File) =>
  /image\/hei[cf]/i.test(file.type) || /\.(heic|heif)$/i.test(file.name);

const carregarImagem = (file: File) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(
        new Error(
          ehHeic(file)
            ? "Foto HEIC (iPhone) não abre neste navegador. No iPhone, envie pelo Safari ou exporte como JPG."
            : "Não foi possível abrir a imagem. Use JPG ou PNG.",
        ),
      );
    };
    img.src = url;
  });

const lerComoDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Não foi possível ler a imagem."));
    reader.readAsDataURL(file);
  });

// folga de 1 KB: o tamanho é estimado pelo base64
const ALVO_BYTES = MIN_BYTES + 1024;

const bytesDoDataUrl = (dataUrl: string) => Math.floor((dataUrl.length - dataUrl.indexOf(",") - 1) * 0.75);

const desenharJpg = (
  img: HTMLImageElement,
  largura: number,
  altura: number,
  qualidade: number,
  comRuido = false,
) => {
  const canvas = document.createElement("canvas");
  canvas.width = largura;
  canvas.height = altura;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Não foi possível converter a imagem. Use JPG ou PNG.");
  ctx.imageSmoothingQuality = "high";
  // fundo branco: JPG não tem transparência e o fundo sairia preto
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, largura, altura);
  ctx.drawImage(img, 0, 0, largura, altura);
  if (comRuido) {
    // ±1 tom por pixel, invisível: imagem de cor lisa comprime abaixo de 10 KB
    // mesmo ampliada, e o Google recusa
    const pixels = ctx.getImageData(0, 0, largura, altura);
    const d = pixels.data;
    for (let i = 0; i < d.length; i += 4) {
      const delta = Math.random() < 0.5 ? -1 : 1;
      d[i] = Math.min(255, Math.max(0, d[i] + delta));
      d[i + 1] = Math.min(255, Math.max(0, d[i + 1] - delta));
    }
    ctx.putImageData(pixels, 0, 0);
  }
  return canvas.toDataURL("image/jpeg", qualidade);
};

export type FotoPronta = {
  dataUrl: string;
  contentType: "image/jpeg" | "image/png";
  /** nome do arquivo com a extensão certa */
  nome: string;
  convertida: boolean;
  ampliada: boolean;
};

/**
 * Devolve a foto pronta para o Google. JPG/PNG que já servem passam como estão;
 * o resto (WebP, AVIF, GIF, HEIC no Safari, arquivo grande ou pequeno demais)
 * vira JPG nas medidas aceitas.
 */
export const prepararFotoParaGoogle = async (file: File, nome = file.name): Promise<FotoPronta> => {
  const img = await carregarImagem(file);
  const plano = planejarFotoParaGoogle({
    tipo: file.type,
    bytes: file.size,
    largura: img.naturalWidth,
    altura: img.naturalHeight,
  });
  if (plano.manterOriginal) {
    return {
      dataUrl: await lerComoDataUrl(file),
      contentType: file.type as "image/jpeg" | "image/png",
      nome,
      convertida: false,
      ampliada: false,
    };
  }

  let { largura, altura } = plano;
  let dataUrl = desenharJpg(img, largura, altura, 0.92);
  for (const lado of LADOS_PARA_PASSAR_DE_10KB) {
    if (bytesDoDataUrl(dataUrl) >= ALVO_BYTES) break;
    const escala = lado / Math.min(largura, altura);
    if (escala <= 1) continue;
    largura = Math.round(largura * escala);
    altura = Math.round(altura * escala);
    dataUrl = desenharJpg(img, largura, altura, 0.95);
  }
  if (bytesDoDataUrl(dataUrl) < ALVO_BYTES) dataUrl = desenharJpg(img, largura, altura, 0.95, true);
  return {
    dataUrl,
    contentType: "image/jpeg",
    nome: nomeComExtensaoJpg(nome),
    convertida: true,
    ampliada: largura > img.naturalWidth,
  };
};

export const avisoDaFoto = (foto: FotoPronta) =>
  foto.ampliada
    ? "A foto era pequena e foi ampliada para o mínimo do Google; pode perder nitidez."
    : foto.convertida
      ? "A foto foi convertida para JPG, formato aceito pelo Google."
      : "";
